// SALDP white-labelling. Added by Sullivan Digital, 2026-10-26. Licensed under the GNU AGPL v3; see LICENSE-AGPL.txt.
/* eslint-disable metabase/no-literal-metabase-strings -- the AGPL attribution must name Metabase */
import { t } from "ttag";
import _ from "underscore";

import { ExternalLink } from "metabase/common/components/ExternalLink";
import { LogoIcon } from "metabase/common/components/LogoIcon";
import { useSelector } from "metabase/redux";
import { useSetting } from "metabase/settings";
import { Box, Divider, Flex, Stack, Text, Title } from "metabase/ui";
import { capitalize } from "metabase/utils/formatting";

import { getBrandName } from "../../selectors";
import { SourceCodeLink } from "../SourceCodeLink/SourceCodeLink";

export const AGPL_LICENSE_URL = "https://www.gnu.org/licenses/agpl-3.0.html";

const RELEASE_TAG = /^v\d+\.\d+\.\d+$/;

/**
 * The About / Legal screen: brand and version, the attribution to Metabase,
 * the modification notice, and the AGPL licence, warranty and source offer.
 * The "Based on Metabase" and copyright lines are deliberately not rebranded.
 */
export function AboutContent() {
  const version = useSetting("version");
  const brandName = useSelector(getBrandName);
  const { tag, date, ...versionExtra } = version ?? {};
  const showBuildDetails = tag != null && !RELEASE_TAG.test(tag);

  return (
    <Flex direction="column" align="center" pb="xl" data-testid="about-screen">
      <Box pb="lg">
        <LogoIcon height={48} />
      </Box>
      <Stack gap="sm" align="center" ta="center">
        <Title order={2}>{brandName}</Title>
        {tag && <Title order={4}>{t`Version ${tag}`}</Title>}
        {date && (
          <Text c="text-secondary" fw="bold">
            {t`Built on ${date}`}
          </Text>
        )}
        {showBuildDetails &&
          _.map(versionExtra, (value, key) => (
            <Text
              key={key}
              c="text-secondary"
              fw="bold"
              style={{ overflowWrap: "anywhere" }}
            >
              {capitalize(key)}: {String(value)}
            </Text>
          ))}

        <Divider size={2} w="100%" />

        <Stack gap="xs" align="center">
          <Text fz="sm">{t`Based on Metabase, © Metabase, Inc. and contributors.`}</Text>
          <Text fz="sm">
            {date
              ? t`Modified by Sullivan Digital. This build: ${date}.`
              : t`Modified by Sullivan Digital.`}
          </Text>
          <Text fz="sm" c="text-secondary">
            {t`Metabase is a Trademark of Metabase, Inc`}
          </Text>
        </Stack>

        <Divider size={2} w="100%" />

        <Stack gap="xs" align="center">
          <Text fz="sm">
            {t`This software is free software, licensed under the GNU Affero General Public License, version 3 (AGPL-3.0).`}
          </Text>
          <Text fz="sm" c="text-secondary">
            {t`It is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the licence for details.`}
          </Text>
          <Flex gap="lg" justify="center">
            <ExternalLink
              href={AGPL_LICENSE_URL}
            >{t`Licence (AGPL-3.0)`}</ExternalLink>
            <SourceCodeLink />
          </Flex>
        </Stack>
      </Stack>
    </Flex>
  );
}
